import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Mail,
  MessageSquare,
  Smartphone,
  ShieldAlert,
  Zap,
  Filter,
  ChevronDown,
  ChevronUp,
  Code,
  Eye,
  X,
  Info,
} from 'lucide-react';
import AdminHeader from '../../components/AdminHeader';
import { apiClient } from '../../App';

/* ------------------------------------------------------------------ */
/*  Small reusable pieces                                              */
/* ------------------------------------------------------------------ */

const CHANNEL_CFG = {
  sms:    { icon: Smartphone,    label: 'SMS',    ring: 'ring-blue-400',   bg: 'bg-blue-50',   text: 'text-blue-700',  border: 'border-blue-200', activeBg: 'bg-blue-600 text-white' },
  email:  { icon: Mail,          label: 'Email',  ring: 'ring-purple-400', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', activeBg: 'bg-purple-600 text-white' },
  in_app: { icon: MessageSquare, label: 'In-App', ring: 'ring-green-400',  bg: 'bg-green-50',  text: 'text-green-700', border: 'border-green-200', activeBg: 'bg-green-600 text-white' },
};

const ChannelBadge = ({ channel, compact }) => {
  const c = CHANNEL_CFG[channel] || CHANNEL_CFG.in_app;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-medium border ${c.bg} ${c.text} ${c.border} ${compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'}`}>
      <Icon className={compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} />{c.label}
    </span>
  );
};

const PriorityBadge = ({ priority, compact }) => {
  if (priority === 'critical') {
    return (
      <span className={`inline-flex items-center gap-1 rounded-full font-medium bg-red-100 text-red-700 border border-red-200 ${compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'}`} data-testid="priority-critical">
        <Zap className={compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} />Critical
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-medium bg-gray-100 text-gray-600 border border-gray-200 ${compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'}`} data-testid="priority-normal">
      Normal
    </span>
  );
};

const QuietHoursBadge = ({ behavior, compact }) => {
  if (behavior === 'bypass') {
    return (
      <span className={`inline-flex items-center gap-1 rounded-full font-medium bg-amber-100 text-amber-700 border border-amber-200 ${compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'}`} data-testid="qh-bypass">
        <ShieldAlert className={compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} />Bypass
      </span>
    );
  }
  return (
    <span className={`text-gray-400 ${compact ? 'text-[10px]' : 'text-xs'}`} data-testid="qh-respect">Respect</span>
  );
};


/* ------------------------------------------------------------------ */
/*  Channel Tabs + Message View (mobile-first)                         */
/* ------------------------------------------------------------------ */

const ChannelTabs = ({ channels }) => {
  const [activeTab, setActiveTab] = useState(channels[0]?.channel || 'in_app');
  const [expanded, setExpanded] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  const active = channels.find(c => c.channel === activeTab) || channels[0];
  if (!active) return null;

  const cfg = CHANNEL_CFG[active.channel] || CHANNEL_CFG.in_app;

  // Truncate preview to ~120 chars for mobile collapsed view
  const body = active.rendered_body || '';
  const isLong = body.length > 120;
  const preview = isLong ? body.slice(0, 120).trim() + '...' : body;

  return (
    <div>
      {/* Tab bar */}
      {channels.length > 1 && (
        <div className="flex gap-1 mb-3" role="tablist">
          {channels.map(ch => {
            const c = CHANNEL_CFG[ch.channel] || CHANNEL_CFG.in_app;
            const Icon = c.icon;
            const isActive = ch.channel === activeTab;
            return (
              <button
                key={ch.channel}
                role="tab"
                aria-selected={isActive}
                onClick={() => { setActiveTab(ch.channel); setExpanded(false); setShowRaw(false); }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px]
                  ${isActive ? c.activeBg : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                data-testid={`tab-${ch.channel}`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{c.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Message content */}
      <div className={`rounded-xl border ${cfg.border} overflow-hidden`}>
        {/* Subject line (email) */}
        {active.subject && (
          <div className={`px-4 py-2 ${cfg.bg} border-b ${cfg.border} text-sm font-medium ${cfg.text}`}>
            {active.subject}
          </div>
        )}

        {/* Body */}
        <div className="p-4 md:p-5 bg-white">
          {showRaw ? (
            <pre className="text-sm bg-gray-900 text-green-400 p-4 rounded-lg font-mono whitespace-pre-wrap leading-relaxed overflow-x-auto" data-testid={`raw-template-${active.channel}`}>
              {active.raw_pattern}
            </pre>
          ) : (
            <div
              className="text-base leading-relaxed text-gray-800 whitespace-pre-wrap"
              data-testid={`rendered-template-${active.channel}`}
            >
              {/* Mobile: show preview or full. Desktop: always full */}
              <span className="md:hidden">
                {expanded || !isLong ? body : preview}
              </span>
              <span className="hidden md:inline">{body}</span>
            </div>
          )}

          {/* Mobile: expand button */}
          {isLong && !expanded && !showRaw && (
            <button
              onClick={() => setExpanded(true)}
              className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-800 min-h-[44px] md:hidden"
              data-testid="expand-message"
            >
              View full message
            </button>
          )}
        </div>

        {/* Action row */}
        <div className={`flex items-center justify-between px-4 py-2 border-t ${cfg.border} bg-gray-50/70`}>
          <button
            onClick={() => { setShowRaw(!showRaw); setExpanded(true); }}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 min-h-[44px]"
            data-testid={`toggle-raw-${active.channel}`}
          >
            {showRaw ? <Eye className="w-3.5 h-3.5" /> : <Code className="w-3.5 h-3.5" />}
            {showRaw ? 'Show rendered' : 'Show raw template'}
          </button>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 min-h-[44px]"
            data-testid={`toggle-details-${active.channel}`}
          >
            <Info className="w-3.5 h-3.5" />
            {showDetails ? 'Hide details' : 'Show details'}
          </button>
        </div>

        {/* Details (variables, etc) — hidden by default on mobile */}
        {showDetails && (
          <div className={`px-4 py-3 border-t ${cfg.border} bg-gray-50/40`}>
            {active.variables.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1.5">Variables</p>
                <div className="flex flex-wrap gap-1.5">
                  {active.variables.map(v => (
                    <span key={v} className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded font-mono">
                      {v}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


/* ------------------------------------------------------------------ */
/*  Desktop Channel Cards (unchanged for lg screens)                   */
/* ------------------------------------------------------------------ */

const ChannelDetailDesktop = ({ ch }) => {
  const [showRaw, setShowRaw] = useState(false);
  const cfg = CHANNEL_CFG[ch.channel] || CHANNEL_CFG.in_app;

  return (
    <div className={`border rounded-lg overflow-hidden ${cfg.border}`}>
      <div className={`flex items-center justify-between px-3 py-2 ${cfg.bg}`}>
        <ChannelBadge channel={ch.channel} />
        <button
          onClick={() => setShowRaw(!showRaw)}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
          data-testid={`toggle-raw-${ch.channel}`}
        >
          {showRaw ? <Eye className="w-3 h-3" /> : <Code className="w-3 h-3" />}
          {showRaw ? 'Rendered' : 'Raw'}
        </button>
      </div>

      <div className="p-3 bg-white">
        {ch.subject && (
          <p className="text-xs text-gray-500 mb-1.5">
            <strong>Subject:</strong> {ch.subject}
          </p>
        )}

        {showRaw ? (
          <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded font-mono whitespace-pre-wrap overflow-x-auto leading-relaxed" data-testid={`raw-template-${ch.channel}`}>
            {ch.raw_pattern}
          </pre>
        ) : (
          <div className="text-sm text-gray-800 bg-zinc-50 p-3 rounded border whitespace-pre-wrap leading-relaxed" data-testid={`rendered-template-${ch.channel}`}>
            {ch.rendered_body}
          </div>
        )}

        {ch.variables.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-1">
            {ch.variables.map(v => (
              <span key={v} className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded font-mono">
                {v}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


/* ------------------------------------------------------------------ */
/*  Template Card                                                      */
/* ------------------------------------------------------------------ */

const TemplateCard = ({ entry }) => (
  <div className="p-4 md:p-5" data-testid={`template-${entry.event_type}`}>
    {/* Header row: name + priority */}
    <div className="flex items-start justify-between gap-2 mb-1">
      <h3 className="font-semibold text-gray-900 text-base">{entry.event_label}</h3>
      <PriorityBadge priority={entry.priority} compact />
    </div>

    {/* Sub row: channel badges + quiet hours */}
    <div className="flex flex-wrap items-center gap-1.5 mb-2">
      {entry.channels.map(c => (
        <ChannelBadge key={c.channel} channel={c.channel} compact />
      ))}
      <QuietHoursBadge behavior={entry.quiet_hours} compact />
      <code className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono hidden sm:inline">
        {entry.event_type}
      </code>
    </div>

    {/* Trigger — single line on mobile */}
    <p className="text-sm text-gray-500 mb-3 line-clamp-2">
      {entry.trigger}
    </p>

    {/* Mobile: channel tabs. Desktop: grid of cards */}
    <div className="md:hidden">
      <ChannelTabs channels={entry.channels} />
    </div>
    <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-3">
      {entry.channels.map(ch => (
        <ChannelDetailDesktop key={ch.channel} ch={ch} />
      ))}
    </div>
  </div>
);


/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */

export default function NotificationTemplates() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filterChannel, setFilterChannel] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterChannel) params.append('channel', filterChannel);
      if (filterPriority) params.append('priority', filterPriority);
      if (filterCategory) params.append('category', filterCategory);
      const response = await apiClient.get(`/admin/notification-templates?${params}`);
      setData(response.data);
      setExpandedGroups(new Set(response.data.entries.map(e => e.category)));
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [filterChannel, filterPriority, filterCategory]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const toggleGroup = (cat) => {
    setExpandedGroups(prev => {
      const n = new Set(prev);
      n.has(cat) ? n.delete(cat) : n.add(cat);
      return n;
    });
  };

  const grouped = useMemo(() => {
    if (!data) return {};
    const g = {};
    for (const e of data.entries) { (g[e.category] ||= []).push(e); }
    return g;
  }, [data]);

  const stats = useMemo(() => {
    if (!data) return {};
    const entries = data.entries;
    return {
      total: entries.length,
      critical: entries.filter(e => e.priority === 'critical').length,
      withSms: entries.filter(e => e.channels.some(c => c.channel === 'sms')).length,
      withEmail: entries.filter(e => e.channels.some(c => c.channel === 'email')).length,
      bypass: entries.filter(e => e.quiet_hours === 'bypass').length,
    };
  }, [data]);

  const hasFilters = filterChannel || filterPriority || filterCategory;

  const clearFilters = () => {
    setFilterChannel('');
    setFilterPriority('');
    setFilterCategory('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />
      <div className="max-w-7xl mx-auto px-4 py-4 md:px-6 md:py-6">

        {/* Header */}
        <div className="mb-4 md:mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900" data-testid="templates-page-title">
            Notification Templates
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Every message the system sends
          </p>
        </div>

        {/* Stats — 2x2 on mobile, 5-col on desktop */}
        {stats.total > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-4 mb-4 md:mb-6">
            <div className="bg-white px-3 py-2.5 md:p-4 rounded-lg shadow-sm border" data-testid="stat-total-templates">
              <p className="text-xs md:text-sm text-gray-500">Templates</p>
              <p className="text-xl md:text-2xl font-bold">{stats.total}</p>
            </div>
            <button className="bg-white px-3 py-2.5 md:p-4 rounded-lg shadow-sm border text-left hover:border-red-300 transition-colors" onClick={() => setFilterPriority(filterPriority === 'critical' ? '' : 'critical')} data-testid="stat-critical">
              <p className="text-xs md:text-sm text-gray-500">Critical</p>
              <p className="text-xl md:text-2xl font-bold text-red-600">{stats.critical}</p>
            </button>
            <button className="bg-white px-3 py-2.5 md:p-4 rounded-lg shadow-sm border text-left hover:border-blue-300 transition-colors" onClick={() => setFilterChannel(filterChannel === 'sms' ? '' : 'sms')} data-testid="stat-with-sms">
              <p className="text-xs md:text-sm text-gray-500">SMS</p>
              <p className="text-xl md:text-2xl font-bold text-blue-600">{stats.withSms}</p>
            </button>
            <button className="bg-white px-3 py-2.5 md:p-4 rounded-lg shadow-sm border text-left hover:border-purple-300 transition-colors" onClick={() => setFilterChannel(filterChannel === 'email' ? '' : 'email')} data-testid="stat-with-email">
              <p className="text-xs md:text-sm text-gray-500">Email</p>
              <p className="text-xl md:text-2xl font-bold text-purple-600">{stats.withEmail}</p>
            </button>
            {/* 5th card hidden on small mobile to keep 2x2, shown on md+ */}
            <div className="hidden md:block bg-white p-4 rounded-lg shadow-sm border" data-testid="stat-bypass">
              <p className="text-sm text-gray-500">QH Bypass</p>
              <p className="text-2xl font-bold text-amber-600">{stats.bypass}</p>
            </div>
          </div>
        )}

        {/* Filter bar — collapsed on mobile */}
        <div className="bg-white rounded-lg shadow-sm mb-4 md:mb-6 border">
          <button
            className="w-full p-3 md:p-4 flex items-center justify-between min-h-[44px]"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="filter-toggle"
          >
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 md:w-5 md:h-5 text-gray-500" />
              <span className="font-medium text-sm md:text-base">
                {hasFilters ? 'Filters (active)' : 'Filter Templates'}
              </span>
              {hasFilters && (
                <span className="bg-blue-100 text-blue-800 text-[10px] px-1.5 py-0.5 rounded-full">
                  {[filterChannel, filterPriority, filterCategory].filter(Boolean).length}
                </span>
              )}
            </div>
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showFilters && (
            <div className="px-3 pb-3 md:px-4 md:pb-4 border-t space-y-3 md:space-y-0 md:grid md:grid-cols-4 md:gap-4 md:pt-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Channel</label>
                <select value={filterChannel} onChange={(e) => setFilterChannel(e.target.value)} className="w-full border rounded-lg px-3 py-2.5 text-sm min-h-[44px]" data-testid="filter-channel">
                  <option value="">All Channels</option>
                  <option value="sms">SMS</option>
                  <option value="email">Email</option>
                  <option value="in_app">In-App</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="w-full border rounded-lg px-3 py-2.5 text-sm min-h-[44px]" data-testid="filter-priority">
                  <option value="">All Priorities</option>
                  <option value="critical">Critical</option>
                  <option value="normal">Normal</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="w-full border rounded-lg px-3 py-2.5 text-sm min-h-[44px]" data-testid="filter-category">
                  <option value="">All Categories</option>
                  {data?.categories?.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                {hasFilters && (
                  <button onClick={clearFilters} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 min-h-[44px]" data-testid="clear-filters">
                    <X className="w-3.5 h-3.5" /> Clear filters
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 border border-red-200 text-sm" data-testid="error-banner">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && <div className="text-center py-12 text-gray-500 text-sm">Loading templates...</div>}

        {/* Grouped Cards */}
        {!loading && Object.entries(grouped).map(([category, entries]) => (
          <div key={category} className="mb-4 md:mb-6" data-testid={`group-${category.toLowerCase().replace(/\s+/g, '-')}`}>
            <button
              onClick={() => toggleGroup(category)}
              className="w-full flex items-center justify-between bg-white border rounded-t-lg px-4 py-3 hover:bg-gray-50 transition-colors min-h-[48px]"
              data-testid={`group-toggle-${category.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center gap-2">
                <h2 className="text-base md:text-lg font-semibold text-gray-900">{category}</h2>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  {entries.length}
                </span>
              </div>
              {expandedGroups.has(category) ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>

            {expandedGroups.has(category) && (
              <div className="border border-t-0 rounded-b-lg divide-y bg-white">
                {entries.map(entry => (
                  <TemplateCard key={entry.event_type} entry={entry} />
                ))}
              </div>
            )}
          </div>
        ))}

        {!loading && data?.total === 0 && (
          <div className="text-center py-12 text-gray-500 text-sm">
            No templates match your filters
          </div>
        )}
      </div>
    </div>
  );
}
